import {Component, effect, inject, signal} from '@angular/core';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {captureException} from '@sentry/angular';
import {CustomerService} from '../../core/services/customer.service';
import {FlexPosService} from '../../core/services/flex-pos.service';
import {BasicModalService} from '../../core/services/basic-modal.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {
  CustomerAccountV2Type,
  CustomerUpsertV2Input
} from '../../core/interfaces/generated/graphql';
import {MatOption, MatSelect} from '@angular/material/select';
import {CvrService} from '../../core/services/cvr.service';

@Component({
  selector: 'app-customer-info-form-modal',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatProgressSpinner,
    MatRadioGroup,
    MatRadioButton,
    MatSelect,
    MatOption
  ],
  templateUrl: './customer-info-form-modal.component.html',
  styleUrl: './customer-info-form-modal.component.scss'
})
export class CustomerInfoFormModalComponent {
  private fb = inject(FormBuilder);
  private flexPosService = inject(FlexPosService);
  readonly dialogRef = inject(MatDialogRef<CustomerInfoFormModalComponent>);
  private customerService = inject(CustomerService);
  private basicModalService = inject(BasicModalService);
  private cvrService = inject(CvrService);

  customerForm: FormGroup;

  loading = signal(false);
  loadingCity = signal(false);
  loadingCompany = signal(false);
  isBusinessCustomer = signal(false);

  data: {
    phoneNumber: string;
    isCreateCustomer: boolean;
  } = inject(MAT_DIALOG_DATA);

  constructor() {
    let customer = null;
    if (!this.data?.isCreateCustomer) {
      customer = this.customerService.selectedCustomer().customer;
    }

    let phoneNumber = customer?.phoneNumber ?? '';
    if (this.data?.phoneNumber && this.isNumeric(this.data.phoneNumber)) {
      phoneNumber = this.data.phoneNumber;
    }

    const customerType = customer?.accountType === '2' ? 'business' : 'private';

    this.customerForm = this.fb.group({
      customerType: [customerType, Validators.required],
      phoneNumber: [phoneNumber, Validators.required],
      email: [customer?.emailForOrderConfirmation, [Validators.required, Validators.email]],
      fullName: [customer?.fullName],

      companyName: [customer?.companyName],
      cvr: [customer?.cvr],
      ean: [customer?.ean],

      address: [customer?.defaultBillingAddress?.address],
      country: [customer?.defaultBillingAddress?.country ?? 'DK'],
      zipcode: [customer?.defaultBillingAddress?.addressZip],
      city: [customer?.defaultBillingAddress?.addressCity],
    }, {validators: this.addressIsFilledOutCorrectValidation()});

    this.customerTypeChange(customerType);

    if (!this.data?.isCreateCustomer) {
      this.customerForm.get('customerType')?.disable();
      effect(() => {
        const customer = this.customerService.selectedCustomer().customer;
        if (customer) {
          this.customerForm.patchValue({
            phoneNumber: customer.phoneNumber,
            email: customer.emailForOrderConfirmation,
            fullName: customer.fullName,
            companyName: customer.companyName,
            cvr: customer.cvr,
            ean: customer.ean,
            address: customer.defaultBillingAddress?.address,
            country: customer.defaultBillingAddress?.country ?? 'DK',
            zipcode: customer.defaultBillingAddress?.addressZip,
            city: customer.defaultBillingAddress?.addressCity
          });
          this.customerForm.controls['email'].disable();
        } else {
          this.customerForm.controls['email'].enable();
        }
        const customerType = customer?.accountType === '2' ? 'business' : 'private';
        this.customerTypeChange(customerType);
      });
    }
  }

  addressIsFilledOutCorrectValidation(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const address = control.get('address')?.value;
      const zipcode = control.get('zipcode')?.value;
      const city = control.get('city')?.value;
      if ((address || zipcode || city) && !(address && zipcode && city)) {
        return {addressIsPartlyFilled: true};
      }
      return null;
    };
  }

  customerTypeChange(value: string) {
    this.isBusinessCustomer.set(value === 'business');
  }

  isNumeric(value: string) {
    return /^-?\d+$/.test(value);
  }

  async onSubmit() {
    if (this.customerForm.valid) {
      this.loading.set(true);
      try {
        if (this.data?.isCreateCustomer) {
          await this.submitCreateCustomer();
        } else {
          await this.submitUpdateCustomer();
        }
      } catch (e) {
        captureException(e, {data: {customerForm: this.customerForm.value}});
        this.basicModalService.showErrorModal('Der skete en fejl. Kunne ikke gemme kunden');
      }
      this.loading.set(false);
    } else {
      if (this.customerForm.hasError('addressIsPartlyFilled')) {
        this.basicModalService.showErrorModal('Udfyld venligst alle felter under adresse');
      }
    }
  }

  async submitCreateCustomer() {
    try {
      const customer = await this.customerService.upsertCustomer(null, this.getDataToSubmit(), this.data?.isCreateCustomer);
      const customerVismaId = customer?.vismaId;
      if (!customerVismaId) {
        console.log('Failed to save customer', customer);
        captureException('Failed to save customer', {data: {customerForm: this.customerForm.value}});
        this.basicModalService.showErrorModal('Der skete en fejl. Kunne ikke gemme kunden');
        return;
      }

      this.customerService.selectedCustomer.set({customer, loading: false});
      this.customerService.selectedCustomerNewsletter.set({
        loading: false, data: {
          isSubscribedToSupervinNewsletter: false,
          isSubscribedToOneToTwoEmailsAWeek: false
        }
      });
      this.flexPosService.forceSyncCustomers();
      await this.flexPosService.addCustomerToJournal(customerVismaId);
      this.closeModal();
      this.basicModalService.showSuccessModal('Kunde oprettet og tilføjet til bon', 'Luk modul', () => {
        this.flexPosService.closeBrowser();
      });
    } catch (error: any) {
      const errorMessage = error?.graphQLErrors?.[0]?.debugMessage;
      if (errorMessage === 'CUSTOMER_ALREADY_EXISTS') {
        this.basicModalService.showErrorModal('Kunden findes allerede');
        return;
      }
      console.log('Failed to save customer', error);
      captureException(error, {data: {customerForm: this.customerForm.value}});
      this.basicModalService.showErrorModal('Der skete en fejl. Kunne ikke gemme kunden');
      return;
    }
  }

  async submitUpdateCustomer() {
    let vismaId = this.customerService.selectedCustomer().customer?.vismaId;
    if (!vismaId) {
      captureException('Missing vismaId', {data: {customerForm: this.customerForm.value}});
      this.basicModalService.showErrorModal('Der skete en fejl. Mangler et vismaId');
      return;
    }
    const customer = await this.customerService.upsertCustomer(vismaId, this.getDataToSubmit(), this.data?.isCreateCustomer);
    const customerVismaId = customer?.vismaId;
    if (!customerVismaId) {
      captureException('Failed to save customer', {data: {customerForm: this.customerForm.value}});
      this.basicModalService.showErrorModal('Der skete en fejl. Kunne ikke gemme kunden');
      return;
    }

    this.customerService.selectedCustomer.set({customer, loading: false});
    this.closeModal();
    this.basicModalService.showSuccessModal('Kunde opdateret', 'Luk modul', () => {
      this.flexPosService.closeBrowser();
    });
  }

  getDataToSubmit(): CustomerUpsertV2Input {
    const customerForm = this.customerForm.value;
    if (!this.data?.isCreateCustomer) {
      const customer = this.customerService.selectedCustomer().customer;
      if (customer) {
        customerForm.email = customer.emailForOrderConfirmation;
      }
    }
    const customer = {
      fullName: customerForm.fullName,
      email: customerForm.email,
      phoneNumber: customerForm.phoneNumber,
      accountType: customerForm.customerType === 'business' ? CustomerAccountV2Type.Company : CustomerAccountV2Type.Private,
      cvr: customerForm.cvr?.toString(),
      ean: customerForm.ean?.toString(),
      companyName: customerForm.companyName,
    } as CustomerUpsertV2Input;

    if (customerForm.address && customerForm.zipcode && customerForm.city) {
      customer.billingAddress = {
        street: customerForm.address,
        zipcode: customerForm.zipcode?.toString(),
        city: customerForm.city,
        countryCode: customerForm.country
      };
    }

    return customer;
  }

  async cvrChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.value.length === 8) {
      this.loadingCompany.set(true);
      const company = await this.cvrService.getCompanyInfoByCVR(target.value, this.customerForm.get('country')?.value);
      if (company) {
        this.customerForm.get('companyName')?.setValue(company.name);
        this.customerForm.get('address')?.setValue(company.address);
        this.customerForm.get('zipcode')?.setValue(company.zipcode);
        this.customerForm.get('city')?.setValue(company.city);
      }
      this.loadingCompany.set(false);
    }
  }

  zipcodeChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.value.length === 4 || target.value.length === 5) {
      this.loadingCity.set(true);
      this.getCityFromZip(target.value, this.customerForm.get('country')?.value).then(city => {
        if (city) {
          this.customerForm.get('city')?.setValue(city);
        }
        this.loadingCity.set(false);
      });
    }
  }

  async getCityFromZip(zipcode: string, countryCode: string = 'DK') {
    let city = null;
    try {
      city = await this.customerService.getCityByZipCode(zipcode, countryCode);
    } catch (error) {
      console.error(error);
    }
    return city;
  }

  closeModal() {
    this.dialogRef.close();
  }
}
